import {
  FormErrorFeedback,
  FormGroupLabel,
  FormSelect,
  HeadingLarge,
  HeadingMedium,
  SubtleButton,
  theme,
  useToast,
  DeprecatedErrorWarningIcon,
} from '@arnold/common';
import styled from '@emotion/styled/macro';
import { Field, Form, Formik, FormikHelpers, FormikValues } from 'formik';
import { equals, isEmpty } from 'ramda';
import { Card, FormControl, FormGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  CreateTeamMutationFn,
  OrganizationQuery,
  OrganizationTeamsQuery,
  UpdateTeamMutationFn,
  useDeleteTeamMutation,
} from '../../generated/hooks';
import { getOrganizationTeams } from '../../graphql/queries';
import ee from '../../lib/eventEmitter';
import { SupervisorTeamSelect } from './SupervisorTeamSelect';
import { TeamsWithSubTeams, TeamWithSubTeams } from './TeamsAndPeople';

type FormProps = {
  organization: NonNullable<OrganizationQuery['organization']>;
  team?: TeamWithSubTeams | null;
  teams?: TeamsWithSubTeams;
  teamNames: string[];
  formStatus?: TeamFormStatus;
  setFormStatus?: (status: TeamFormStatus) => void;
  onDeletionComplete?: () => void;
  defaultSupervisorTeamId?: string;
  respondents: NonNullable<OrganizationTeamsQuery['organizationTeams']>[0]['members'];
  teamLeaderIds: string[];
  saveTeam?: CreateTeamMutationFn | UpdateTeamMutationFn;
  addRespondent: () => void;
  latestCreatedEmployeeId?: string;
  setLatestCreatedEmployeeId: (id: string | undefined) => void;
  disableCreatingNewPerson?: boolean;
};

export type TeamFormStatus = {
  somethingChanged: boolean;
  saveDisabled: boolean;
  save: () => void;
};

const TeamForm = ({
  organization,
  team,
  formStatus,
  setFormStatus,
  onDeletionComplete,
  teams,
  defaultSupervisorTeamId,
  respondents,
  teamLeaderIds,
  saveTeam,
  teamNames,
  addRespondent,
  latestCreatedEmployeeId,
  setLatestCreatedEmployeeId,
  disableCreatingNewPerson,
}: FormProps) => {
  const { addToast } = useToast();
  const { t } = useTranslation('organizationStructure');

  const TeamFormValidationSchema = Yup.object().shape({
    name: Yup.string()
      .required(t('organizationStructure:enterName'))
      .test(
        'unique-name',
        t('nameAlreadyExists'),
        (value) =>
          !teamNames.some((name) => name.localeCompare(value?.trim() || '', undefined, { sensitivity: 'base' }) === 0),
      )
      .test('no-semicolon', t('noSemicolonAllowed'), (value) => !(value && value.includes(';'))),
  });
  const [deleteTeam] = useDeleteTeamMutation({
    refetchQueries: [getOrganizationTeams],
    awaitRefetchQueries: true,
  });

  const handleSaveTeam = () => async (values: FormikValues, formikHelpers: FormikHelpers<any>) => {
    const { setSubmitting } = formikHelpers;
    try {
      setSubmitting(true);
      if (!team || !team.id) {
        await (saveTeam as CreateTeamMutationFn)({
          variables: {
            organizationId: organization.id,
            teamName: values.name?.trim(),
            supervisorTeamId: values.supervisorTeamId === 'unset' ? null : values.supervisorTeamId,
            respondentId:
              values.teamLeaderId === 'unset' || values.teamLeaderId === 'waiting_for_new_person'
                ? null
                : values.teamLeaderId,
          },
        });
      }

      if (team) {
        await (saveTeam as UpdateTeamMutationFn)({
          variables: {
            teamId: team.id,
            input: {
              teamName: values.name?.trim(),
              supervisorTeamId: values.supervisorTeamId === 'unset' ? null : values.supervisorTeamId,
              teamLeaderId:
                values.teamLeaderId === 'unset' || values.teamLeaderId === 'waiting_for_new_person'
                  ? null
                  : values.teamLeaderId,
            },
          },
        });
      }
      setSubmitting(false);
    } catch (e) {
      setSubmitting(false);
    }
  };

  const options = [
    ...(disableCreatingNewPerson ? [] : [{ value: 'add_new_person', label: t('teams:addANewPerson') }]),
    { value: 'unset', label: t('teams:noManager') },
    ...respondents
      .map((respondent) => {
        return {
          value: respondent.id,
          label: `${respondent.surname} ${respondent.firstname}`,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const removeTeams = (teams: TeamsWithSubTeams, teamId: string | undefined): TeamsWithSubTeams => {
    return teams
      .filter((team) => team.id !== teamId && team.structureId !== '')
      .map((team) => ({
        ...team,
        subTeams: removeTeams(team.subTeams, teamId),
      }));
  };
  const teamsForSupervisorTeamSelect = removeTeams(teams!, team?.id);

  const supervisorTeamId = team?.supervisorTeam?.id || defaultSupervisorTeamId || 'unset';
  const teamLeaderId = team?.leader?.id || 'unset';

  return (
    <Formik
      initialValues={{
        name: (team && team.name) || '',
        supervisorTeamId,
        teamLeaderId,
      }}
      validationSchema={TeamFormValidationSchema}
      onSubmit={handleSaveTeam()}
    >
      {({
        values,
        errors,
        touched,
        handleSubmit,
        handleChange,
        handleBlur,
        isSubmitting,
        initialValues,
        setFieldValue,
      }) => {
        const newFormStatus = {
          somethingChanged: !equals(values, initialValues),
          save: handleSubmit,
          saveDisabled: !isEmpty(errors) || isSubmitting,
        };

        if (!equals(newFormStatus, formStatus)) {
          setFormStatus?.(newFormStatus);
        }

        if (values.teamLeaderId === 'add_new_person') {
          addRespondent();
          setFieldValue('teamLeaderId', 'waiting_for_new_person');
        }
        if (latestCreatedEmployeeId && values.teamLeaderId === 'waiting_for_new_person') {
          setFieldValue('teamLeaderId', latestCreatedEmployeeId);
          setLatestCreatedEmployeeId(undefined);
        }

        return (
          <Form onSubmit={handleSubmit} data-icom={'form-team'} data-cy="team-form">
            <HeadingLarge className="m-0 mb-8">{team ? team.name : t('peopleChart:createTeam')}</HeadingLarge>
            <Card className="p-8">
              <Card.Body className="p-0">
                <HeadingMedium className="m-0 mb-7">{t('teamInformationsTitle')}</HeadingMedium>
                <FormGroup>
                  <FormGroupLabel>
                    {t('teamNameLabel')}
                    <RedAsterisk />
                  </FormGroupLabel>
                  <FormControl
                    type="text"
                    name="name"
                    value={values.name || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.name && !!errors.name}
                  />
                  <FormErrorFeedback error={errors.name} />
                </FormGroup>
                <FormGroup data-cy="supervisor-team-select">
                  <FormGroupLabel>{t('superirorTeamLabel')}</FormGroupLabel>
                  <Field
                    component={SupervisorTeamSelect}
                    name="supervisorTeamId"
                    teams={[
                      {
                        id: 'unset',
                        name: `${organization.name}`,
                        numberOfEmployees: respondents.length,
                        subTeams: teamsForSupervisorTeamSelect,
                      },
                    ]}
                    isSearchable
                  />
                </FormGroup>
                <FormGroup data-cy="team-leader-select">
                  <FormGroupLabel>{t('teamManagerLabel')}</FormGroupLabel>
                  <TeamManagerWrapper hasCreateOption={!disableCreatingNewPerson}>
                    <Field
                      component={FormSelect}
                      name="teamLeaderId"
                      options={options}
                      handleChange={handleChange}
                      onBlur={handleBlur}
                      id={'teamLeader-select'}
                      value={options.find((option) => option.value === values.teamLeaderId)}
                      isSearchable={true}
                      noOptionsMessage={() => t('processDetail:noOptions')}
                    />
                  </TeamManagerWrapper>
                </FormGroup>
              </Card.Body>
            </Card>
            {team && team.name && (
              <Card className="p-8">
                <Card.Body className="p-0">
                  <HeadingMedium className="m-0 mb-7">{t('deleteTeamTitle')}</HeadingMedium>
                  <DangerZoneWarning
                    heading={t('deletionWarningTitle', { name: team.name })}
                    descriptionPoints={
                      [
                        t('alertTeamDeleteInfo1'),
                        team.members.length > 0
                          ? t('alertTeamDeleteInfo2', { count: team.members.length, postProcess: 'interval' })
                          : '',
                        team.subTeams.length > 0 ? t('alertTeamDeleteInfo3') : '',
                      ].filter(Boolean) as string[]
                    }
                  />
                  <SubtleButton
                    style={{ marginTop: '32px' }}
                    onClick={(e) => {
                      e.preventDefault();
                      ee.emitModalDeleteTeam(
                        team.name!,
                        async () => {
                          await deleteTeam({
                            variables: {
                              teamId: team.id,
                            },
                          });
                          addToast(
                            t('organizationStructure:teamDeleted', {
                              name: team.name,
                            }),
                          );
                          onDeletionComplete?.();
                        },
                        undefined,
                      );
                    }}
                    data-cy="delete-team-button"
                    color="danger"
                  >
                    {t('delete')}
                  </SubtleButton>
                </Card.Body>
              </Card>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default TeamForm;

const RedAsterisk = () => <span style={{ color: theme.colors.emotionDanger.default }}>*</span>;

const DangerZoneWarning = ({ heading, descriptionPoints }: { heading: string; descriptionPoints: string[] }) => (
  <StyledDangerZoneWarning>
    <DeprecatedErrorWarningIcon />
    <div>
      <div className="dangerZoneHeading">{heading}</div>
      <ul>
        {descriptionPoints.map((point) => (
          <li key={point}>{point}</li>
        ))}
      </ul>
    </div>
  </StyledDangerZoneWarning>
);

const StyledDangerZoneWarning = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: ${theme.spacing.f};
  padding: ${theme.spacing.f};
  border: ${theme.spacing.a} solid ${theme.colors.emotionDanger.default};
  border-radius: ${theme.spacing.d};
  background: ${theme.colors.emotionDanger.background};
  color: ${theme.colors.emotionDanger.default};
  svg > path {
    fill: ${theme.colors.emotionDanger.default};
  }

  & > div {
    margin-left: ${theme.spacing.f};
  }

  ul {
    margin: 0;
    padding-left: ${theme.spacing.f};
  }

  .dangerZoneHeading {
    font-weight: ${theme.typography.body.medium.bold.fontWeight};
    margin-bottom: ${theme.spacing.c};
  }
`;

const TeamManagerWrapper = styled.div<{ hasCreateOption: boolean }>`
  ${({ hasCreateOption }) =>
    hasCreateOption &&
    `
  [class*='MenuList'] > div:first-child {
  position: relative;
  display: flex;
  align-items: center;
  border-bottom: none;
}

[class*='MenuList'] > div:first-child::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 3.5%;
  right: 3.5%;
  width: auto;
  height: 1px;
  background-color: ${theme.colors.borderMain.default};
}

  [class*='MenuList'] > div:first-child:before {
    content: '+';
    margin-right: 8px;
    color: ${theme.colors.iconText.primary};
    font-size: 22px;
    height: 22px;
    display: flex;
    align-items: center;
  }
  `}
`;
